/* One workout, transcribed from `Builder planu - tydzien - programator.dc.html`'s `bodySt` and
 * everything under it: the header, the scrolling body of warm-up and main work, and the day's load
 * module along the bottom.
 *
 * ── The card is a LAYER, on BOTH canvases ────────────────────────────────────────────────────
 * The mock draws one box per column (`cardSt`, owned by `DayColumn` on the week and `CycleColumn`
 * on the sequence canvas) and `.card` below is the mock's `bodySt` — transparent, no edge, no
 * radius, no shadow. `.standalone`, which re-declared `cardSt` here for the old free-standing tape
 * tile, went with that tile on 13 September 2026. See `SessionCardProps.box`.
 *
 * ── `--text-3` is used, and the reading floor is gone ────────────────────────────────────────
 * This file carried a 10px floor and a rule against tertiary ink. Both produced a screen the owner
 * rejected beside the mock on 8 September 2026. The mock's sizes ship: 8.5px on the muscle line,
 * 9px on the two block labels, 9.5px on the badges. The shortfall against WCAG's text-size
 * guidance is real and is the designer's to answer; every fact drawn at those sizes is also carried
 * by something larger beside it.
 *
 * ── What is no longer drawn ──────────────────────────────────────────────────────────────────
 * · **The ⋯ menu.** The mock's header carries a grip (removed by the owner on 15 September 2026), a title, a duplicate and an ×, and nothing
 *   else. Everything the ⋯ held — "Programuj dzień", the rename, and the seven move targets — is
 *   the card's CONTEXT MENU: right-click it, or press the keyboard menu key while any control
 *   inside it has focus. The menu is a full equivalent of the drag, which view ADR §12 requires.
 * · **The dashed rename underline** and its button. The title is plain text; renaming is a context
 *   menu entry, and the field it opens stands in the title's own box.
 * · **The D-badge.** The column header directly above prints where the day falls, on BOTH canvases
 *   since the sequence mock gave its columns a "DZIEŃ n" head. The field is gone from the view
 *   model; the number still words the move menu's entries.
 * · **The superset frame and its label.** The mock draws no rail and no indent on the week card:
 *   a superset is one list position carrying `2a` / `2b` in the accent. The WORD is still rendered,
 *   visually hidden, so the pairing reaches a screen reader as language rather than as geometry.
 * · **"bez ćwiczeń — nie zapisze się".** A day carrying no exercise is dropped by the serialiser and
 *   the mock has no picture of one. The sentence is announced and drawn nowhere. Flagged: a sighted
 *   trainer who has just pressed "+ Trening" no longer sees that walking away loses the day.
 * · **The warm-up's "poza licznikiem serii" footnote.** Not in the mock. Announced only.
 * · **The warm-up DURATION.** The mock prints "8 min" at the end of the warm-up label; nothing in
 *   the contract carries one, so the element is emitted with nothing in it — which is exactly what
 *   the mock does when `d.warm` is empty.
 */

/* The mock's `bodySt`: `position:absolute; inset:0; display:flex; flex-direction:column;
 * overflow:hidden; min-height:0`. The box around it owns every visible byte. */
.card {
  position: absolute;
  inset: var(--space-0);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.card:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--outline-inset);
}

/* ── The header: `11px 12px 10px`, a hairline under it, gap 8, items to the TOP ─────────────── */
.head {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: flex-start;
  padding: var(--space-11px) var(--space-3) var(--space-10px);
  border-bottom: var(--border-hairline) solid var(--border);
}

.headText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3px);
  min-width: 0;
}

/* 14.5 / 800 / -.02em, line-height 1.15, one line, ellipsised. A second line would push the load
 * module down, and the tallest card sets the height of every column beside it. */
.title {
  margin: var(--space-0);
  overflow: hidden;
  font-size: var(--fs-field);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  line-height: var(--lh-plan-hero);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The rename field, standing in the title's own box at the title's own type so the header does not
 * change height when it opens. */
.titleInput {
  width: 100%;
  min-width: 0;
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-field);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  line-height: var(--lh-plan-hero);
  color: var(--text);
  background: transparent;
  border-width: var(--space-0);
  border-bottom-width: var(--border-hairline);
  border-style: solid;
  border-color: var(--border-2);
  outline: none;
}

/* "5 ćwiczeń · 16 serii" — 11 / 600, tertiary, tabular. */
.meta {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* The two trailing controls: a 22×22 radius-7 duplicate and a 15px ×, both in `--text-3`. The mock
 * gives the header one 8px gap throughout, so they are flex children of the header itself. */
.headAction {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: var(--space-0);
  color: var(--text-3);
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-7px);
}

.headAction:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.headAction:hover:not([aria-disabled="true"]) {
  color: var(--text-2);
}

.headAction[aria-disabled="true"] {
  cursor: not-allowed;
}

.headActionGlyph {
  display: block;
  width: 14px;
  height: 14px;
}

/* The mock's × is a bare glyph at 15px with `line-height: 1`, not a bordered box, so it takes no
 * 22px square of its own. */
.headActionCross {
  display: block;
  font-size: var(--fs-session);
  line-height: var(--lh-none);
}

.cross {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 15px;
  padding: var(--space-0);
  color: var(--text-3);
  cursor: pointer;
  background: transparent;
  border: 0;
}

.cross:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.cross:hover:not([aria-disabled="true"]) {
  color: var(--text-2);
}

.cross[aria-disabled="true"] {
  cursor: not-allowed;
}

/* ── The scrolling body: `9px 12px 4px`, gap 8, vertical scroll only ────────────────────────── */
.body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  min-height: 0;
  padding: var(--space-9px) var(--space-3) var(--space-1);
  overflow: hidden auto;
}

/* The warm-up block: its own `--blue-soft` surface at radius 8, `8px 9px`, rows 2px apart. */
.warmup {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-2px);
  padding: var(--space-2) var(--space-9px);
  background: var(--warm-bg);
  border-radius: var(--radius-8px);
}

/* "ROZGRZEWKA" — 9 / 800 / .09em, uppercase, in the structural blue. The mock puts a bold duration
 * at the end of the same row; nothing in the contract carries one, so the row is the label alone. */
.warmupTitle {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
  margin: var(--space-0);
  overflow: hidden;
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-column);
  text-transform: uppercase;
  color: var(--warm-blue);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The main work is a plain column: the mock draws no separator between the block and the rows, and
 * the row's own `border-top` is what divides them. */
.exercises {
  display: flex;
  flex-direction: column;
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.position {
  display: flex;
  flex-direction: column;
}

/* ── One row, in either block ──────────────────────────────────────────────────────────────────
 * A warm-up row is `padding-top: 6px` with no rule above it; a main row is `7px 0` and carries a
 * `--border` hairline from the SECOND row on — counted across the whole main list, so a superset
 * pair is divided from what precedes it exactly as two singles would be. */
.row {
  display: flex;
  gap: var(--space-9px);
  align-items: center;
}

/* Main work: `7px 0`, and a hairline above every row but the first. */
.rowMain {
  padding: var(--space-7px) var(--space-0);
}

.rowRuled {
  border-top: var(--border-hairline) solid var(--border);
}

/* A warm-up row: `padding-top: 6px` and nothing else — the mock's `warmExRows` draws no rule
 * between them, because the tinted block already groups them. */
.warmRow {
  padding: var(--space-6px) var(--space-0) var(--space-0);
}

/* The position badge: min-width 21, 19 tall, `0 4px`, radius 6, 9.5 / 700 tabular, `--bg-inset`
 * under a `--border` hairline. */
.badge {
  display: grid;
  flex: none;
  place-items: center;
  min-width: 21px;
  height: 19px;
  padding: var(--space-0) var(--space-1);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xs);
  font-variant-numeric: tabular-nums;
}

/* A superset member: the mock puts the ACCENT on the badge's edge and its ink, and draws nothing
 * else — no frame, no rail, no indent. It is the mock's own value, not `--superset`. */
.badgeBracket {
  color: var(--accent);
  border-color: var(--accent);
}

/* The warm-up's `R`: transparent, a blue hairline, blue ink, and no tabular figures — it is a
 * letter, and the mock's `warmExRows` omits `fontVariantNumeric` where `exRows` sets it. */
.badgeWarmup {
  color: var(--warm-blue);
  background: transparent;
  border-color: var(--warm-blue);
  font-variant-numeric: normal;
}

/* The 40×30 still at radius 8 under a `--border` hairline. `shared/ui/photoBox` declares the
 * `object-fit` and the sunken ground, so an image that has not arrived, 404s or is blocked leaves
 * the ground showing and the box never resizes. */
.thumb {
  display: block;
  flex: none;
  width: 40px;
  height: 30px;
  border-radius: var(--radius-8px);
}

.photoThumb {
  border: var(--border-hairline) solid var(--border);
}

/* The mock's fallback: the muscle's own hue at 14%, carrying a 13×13 glyph in the same hue. It is a
 * `grid` with `place-items: center` and NO border — the mock draws one only on a real photograph. */
.placeholder {
  display: grid;
  place-items: center;
}

.glyph {
  display: block;
  width: 13px;
  height: 13px;
}

.rowText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
}

/* 12 / 700 / -.01em, line-height 1.2, one line, ellipsised. */
.exercise {
  overflow: hidden;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-heading);
  line-height: var(--lh-display);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The muscle line: 8.5 / 600 / .07em uppercase in tertiary ink on a 12px line box, with a 6×6
 * radius-2 dot 5px to its left. The dot's colour is the group's and nothing is read from it — the
 * group's name is the text beside it. */
.chip {
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-pl-sub);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-block);
  line-height: var(--lh-mg-line);
  text-transform: uppercase;
  color: var(--text-3);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chipDot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: var(--space-5px);
  vertical-align: middle;
  border-radius: var(--radius-hair);
}

/* ── The load module: the card's footer ────────────────────────────────────────────────────────
 * `10px 12px 12px`, a hairline above it, `--bg-card` under it so it never shows the body scrolling
 * beneath, rows 5px apart. */
.load {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-5px);
  padding: var(--space-10px) var(--space-3) var(--space-3);
  background: var(--bg-card);
  border-top: var(--border-hairline) solid var(--border);
}

.loadTitle {
  margin: var(--space-0);
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-tag);
  text-transform: uppercase;
  color: var(--text-2);
}

.loadRow {
  display: grid;
  grid-template-columns: 48px 1fr 30px;
  column-gap: var(--space-7px);
  align-items: center;
}

.loadLabel {
  overflow: hidden;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.loadTrack {
  display: block;
  height: 5px;
  overflow: hidden;
  background: var(--bg-sunken);
  border-radius: var(--radius-bar);
}

.loadFill {
  display: block;
  height: 100%;
  border-radius: var(--radius-bar);
}

.loadValue {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* The reserve the module holds while the catalogue read is open. The GATE hides the bars and never
 * the box — a fast answer draws no skeleton at all and the geometry is identical either way. */
.loadLabelBar {
  display: block;
  height: 8px;
  background: var(--bg-sunken);
  border-radius: var(--radius-bar);
}

.loadValueBar {
  display: block;
  height: 8px;
  background: var(--bg-sunken);
  border-radius: var(--radius-bar);
}

/* ── The drag SOURCE's own end ─────────────────────────────────────────────────────────────────
 * The card cannot be unmounted mid-gesture, so the ghost is drawn OVER it: the content is hidden
 * and a dashed panel carrying "Sesja w ruchu" and the session's name takes its place. */
.sourceGhost {
  position: absolute;
  inset: var(--space-0);
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  align-items: center;
  justify-content: center;
  background: var(--bg-inset);
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--radius-md);
}

.sourceLabel {
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-tag);
  text-transform: uppercase;
  color: var(--text-3);
}

.sourceName {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

/* In the accessibility tree, drawn nowhere: the superset's word, the warm-up footnote, the "this
 * day will not save" note, and every refusal a header control carries. */
.hidden {
  position: absolute;
  width: var(--space-1px);
  height: var(--space-1px);
  margin: var(--space-0);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* The empty day's invitation. The body is a column that otherwise holds rows, so the state takes
 * what is left of it rather than being pinned to the top: an invitation hugging the header reads as
 * a caption on the header, not as an answer about the card. `EmptyState` centres its own contents,
 * so this says how much room it is given and nothing else. */
.emptyDay {
  flex: 1;
}
